import { useMemo, useEffect } from "react";

import { ServiceContract } from "../types/service.type";
import { ContractPaymentData } from "../api/types/contract.types";
import { createContractState } from "../types/contractState.types";
import {
  canReviewRequest,
  canContinueToPayment,
  hasPaidMilestone,
  isContractPaidWithPaymentData,
} from "../utils/contractBusinessLogic";

interface UseContractStateParams {
  contractDetails: ServiceContract | null | undefined;
  paymentData: ContractPaymentData | null;
  isProvider: boolean;
  onContractPaidChange?: (isContractPaid: boolean) => void;
}

/**
 * Hook that manages contract state calculations and derived values.
 * Memoizes contractState and calculates business logic flags.
 */
export const useContractState = ({
  contractDetails,
  paymentData,
  isProvider,
  onContractPaidChange,
}: UseContractStateParams) => {
  // Memoize contract state to avoid duplicate creation
  const contractState = useMemo(() => {
    if (!contractDetails) return null;
    return createContractState(contractDetails, paymentData);
  }, [contractDetails, paymentData]);

  // Calculate derived values
  const milestonePaid = useMemo(() => {
    if (!contractDetails) return false;
    return hasPaidMilestone(contractDetails.milestones);
  }, [contractDetails?.milestones]);

  const isContractPaid = useMemo(() => {
    if (!contractState) return false;
    return isContractPaidWithPaymentData(contractState, milestonePaid);
  }, [contractState, milestonePaid]);

  const canProceedToPayment = useMemo(() => {
    if (!contractState) return false;
    return canContinueToPayment(contractState, isProvider);
  }, [contractState, isProvider]);

  const canReview = useMemo(() => {
    if (!contractDetails) return false;
    return canReviewRequest(contractDetails);
  }, [contractDetails]);

  // Notify parent of contract paid status changes
  useEffect(() => {
    if (!contractDetails || !onContractPaidChange) return;
    onContractPaidChange(isContractPaid);
  }, [contractDetails, isContractPaid, onContractPaidChange]);

  return {
    contractState,
    milestonePaid,
    isContractPaid,
    canProceedToPayment,
    canReview,
  };
};
